import React, { useState } from 'react';
import { Mic, ArrowRight, ShieldCheck, TrendingUp, Keyboard } from 'lucide-react';
import { categorizeNiche } from '../utils/nicheUtils';

interface Props {
  onComplete: (nicheName: string, serviceDescription: string) => void;
}

export default function ReverseOnboarding({ onComplete }: Props) {
  const [isRecording, setIsRecording] = useState(false);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [transcribedText, setTranscribedText] = useState('');

  const handleMicClick = () => {
    if (!isRecording) {
      if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
        alert('Seu navegador não suporta reconhecimento de voz. Por favor, use o teclado.');
        setShowKeyboard(true);
        return;
      }

      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.lang = 'pt-BR';
      recognition.continuous = false;
      recognition.interimResults = false;

      recognition.onstart = () => {
        setIsRecording(true);
        setTranscribedText('');
      };

      recognition.onresult = (event: any) => {
        const text = event.results[0][0].transcript;
        setTranscribedText(text);
      };

      recognition.onerror = (event: any) => {
        console.error('Speech recognition error', event.error);
        setIsRecording(false);
      };

      recognition.onend = () => {
        setIsRecording(false);
      };

      recognition.start();
    }
  };

  const handleSubmit = () => {
    // Dynamically extract niche based on input
    const extractedNiche = categorizeNiche(transcribedText);
    onComplete(extractedNiche, transcribedText);
  };

  return (
    <div className="min-h-screen bg-zinc-900 text-white flex flex-col items-center justify-center p-6 relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-brand/20 rounded-full blur-[100px] pointer-events-none" />

      <div className="relative z-10 w-full max-w-md flex flex-col items-center space-y-8">
        
        <div className="flex items-center gap-2">
          <div className="bg-brand p-2 rounded-xl">
            <TrendingUp className="h-6 w-6 text-white" />
          </div>
          <span className="font-black text-3xl tracking-tight text-white">R MAX</span>
        </div>

        <h1 className="text-3xl font-black text-center leading-tight">
          O que você precisa <br/><span className="text-brand">orçar hoje?</span>
        </h1>
        
        <p className="text-zinc-400 text-center text-sm px-6">
          Esqueça formulários longos. Apenas aperte o botão e diga o seu serviço em voz alta.
        </p>

        <div className="flex flex-col items-center gap-6">
          <button
            onClick={handleMicClick}
            className={`relative group transition-transform duration-300 ${isRecording ? 'scale-110' : 'hover:scale-105'}`}
          >
            {isRecording && (
              <>
                <div className="absolute inset-0 bg-brand/30 rounded-full animate-ping" />
                <div className="absolute inset-0 bg-brand/20 rounded-full animate-pulse blur-xl" />
              </>
            )}
            <div className={`relative z-10 w-32 h-32 rounded-full flex items-center justify-center shadow-2xl transition-colors duration-300 ${
              isRecording ? 'bg-brand shadow-brand/50' : 'bg-zinc-800 border-2 border-zinc-700 shadow-brand/20 group-hover:border-brand/50 group-hover:bg-zinc-700'
            }`}>
              <Mic className={`h-12 w-12 ${isRecording ? 'text-white animate-pulse' : 'text-brand'}`} />
            </div>
          </button>

          {!isRecording && (!transcribedText || showKeyboard) && (
            <div className="w-full flex flex-col items-center animate-in fade-in slide-in-from-top-2 duration-500">
              <button
                onClick={() => setShowKeyboard(!showKeyboard)}
                className="mt-8 flex items-center gap-2 text-zinc-500 hover:text-brand uppercase tracking-widest text-xs transition-colors"
              >
                <Keyboard className="w-4 h-4" />
                {showKeyboard ? 'Esconder Teclado' : 'Usar Teclado'}
              </button>

              <div className={`${!showKeyboard ? 'hidden' : 'w-full mt-6 animate-in zoom-in-95 duration-200'}`}>
                <input
                  type="text"
                  autoFocus
                  placeholder="Ou digite seu serviço aqui..."
                  className="w-full bg-zinc-800 border-2 border-brand/20 focus:border-brand text-white px-6 py-4 rounded-2xl text-center placeholder:text-zinc-600 outline-none transition-all shadow-xl shadow-brand/5"
                  value={transcribedText}
                  onChange={(e) => setTranscribedText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && transcribedText && handleSubmit()}
                />
                <p className="text-[10px] text-zinc-500 mt-2 text-center uppercase tracking-widest font-bold">Pressione Enter para processar</p>
              </div>
            </div>
          )}
        </div>

        {/* Transcription Preview (Appears after recording) */}
        {transcribedText && !showKeyboard && (
          <div className="w-full bg-zinc-800/50 border border-zinc-700 p-4 rounded-2xl mb-8 backdrop-blur-sm animate-in fade-in slide-in-from-bottom-4">
            <p className="text-zinc-300 text-sm font-medium italic text-center leading-relaxed">
              "{transcribedText}"
            </p>
            <button 
              onClick={handleSubmit}
              className="mt-4 w-full bg-brand text-zinc-900 font-black py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-brand-dark transition-colors"
            >
              Gerar Orçamento
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Aviso de Privacidade com distanciamento corrigido */}
        {!transcribedText && (
          <div className="mt-20 flex items-center gap-2 text-zinc-500 text-[10px] bg-zinc-800/50 px-4 py-2 rounded-full">
            <ShieldCheck className="h-4 w-4 text-brand shrink-0" />
            <p className="leading-tight text-center max-w-sm">
              Sua voz e dados são lidos pelo sistema apenas para <br/>
              cálculo de lucro local e não são compartilhados.
            </p>
          </div>
        )}

      </div>
    </div>
  );
}
